import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Pencil, Trash, X } from '@phosphor-icons/react';
import { getCategories, createCategory, updateCategory, deleteCategory } from '../../utils/api';
import { formatDate } from '../../utils/helpers';
import { toast } from 'sonner';

const AdminCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({ name: '' });

  const fetchCategories = async () => {
    try {
      const res = await getCategories();
      setCategories(res.data.data || []);
    } catch (error) {
      toast.error('Failed to fetch categories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const openModal = (category = null) => {
    setEditingCategory(category);
    setFormData({ name: category?.name || '' });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingCategory(null);
    setFormData({ name: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingCategory) {
        await updateCategory(editingCategory.id, formData);
        toast.success('Category updated successfully');
      } else {
        await createCategory(formData);
        toast.success('Category created successfully');
      }
      closeModal();
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;
    
    try {
      await deleteCategory(id);
      toast.success('Category deleted successfully');
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete category');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="font-display text-2xl text-secondary">Categories</h1>
          <p className="font-body text-sm text-text-secondary">{categories.length} categories</p>
        </div>
        
        <button
          onClick={() => openModal()}
          data-testid="add-category-btn"
          className="btn-primary flex items-center gap-2"
        >
          <Plus size={18} />
          Add Category
        </button>
      </div>

      {/* Categories Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          [...Array(6)].map((_, i) => (
            <div key={i} className="admin-card animate-pulse">
              <div className="h-20 bg-gray-100 rounded" />
            </div>
          ))
        ) : categories.length === 0 ? (
          <div className="col-span-full text-center py-12">
            <p className="font-body text-text-secondary mb-4">No categories found</p>
            <button onClick={() => openModal()} className="btn-secondary">
              Create First Category
            </button>
          </div>
        ) : (
          categories.map((category) => (
            <motion.div
              key={category.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="admin-card"
              data-testid={`category-card-${category.id}`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-display text-lg text-secondary mb-1">{category.name}</h3>
                  <p className="font-body text-xs text-text-muted">
                    Created {formatDate(category.created_at)}
                  </p>
                </div>
                
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => openModal(category)}
                    data-testid={`edit-category-${category.id}`}
                    className="p-2 text-text-muted hover:text-primary transition-colors"
                  >
                    <Pencil size={18} />
                  </button>
                  <button
                    onClick={() => handleDelete(category.id)}
                    data-testid={`delete-category-${category.id}`}
                    className="p-2 text-text-muted hover:text-red-500 transition-colors"
                  >
                    <Trash size={18} />
                  </button>
                </div>
              </div>
            </motion.div>
          ))
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white w-full max-w-md"
          >
            <div className="flex items-center justify-between p-4 border-b border-border-default">
              <h3 className="font-display text-xl text-secondary">
                {editingCategory ? 'Edit Category' : 'Add Category'}
              </h3>
              <button onClick={closeModal}>
                <X size={24} className="text-text-muted hover:text-secondary" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block font-body text-sm text-text-secondary mb-2">
                  Category Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ name: e.target.value })}
                  required
                  data-testid="category-name-input"
                  className="w-full px-4 py-3 border border-border-default focus:border-primary focus:outline-none font-body"
                  placeholder="e.g., Necklaces"
                />
              </div>
              
              <div className="flex gap-4 pt-4">
                <button type="button" onClick={closeModal} className="btn-secondary flex-1">
                  Cancel
                </button>
                <button type="submit" data-testid="save-category-btn" className="btn-primary flex-1">
                  {editingCategory ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminCategories;
